import { useState, useEffect, useCallback } from "react";
import { useAdxQuery } from "../lib/useAdxQuery";
import { getActivityFeed, getActivityMeta } from "../lib/apiClient";
import { formatTime, splitEmail, RANGES, rangePhrase } from "../lib/format";
import PageHead from "../components/PageHead";
import { Icon } from "../components/Icon";
import { ErrorAlert, Loading, Empty, Field } from "../components/States";

const EVENT_TAG = {
  claude_chat_created:    "tag--new",
  claude_chat_viewed:     "tag--nav",
  api_key_created:        "tag--warn",
  scoped_api_key_deleted: "tag--warn",
};

const PAGE_SIZE = 50;

export default function ActivityView() {
  const { run, loading, error } = useAdxQuery();
  const [rows, setRows] = useState([]);
  const [take, setTake] = useState(PAGE_SIZE);
  const [hasMore, setHasMore] = useState(false);
  const [users, setUsers] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [user, setUser] = useState("");
  const [eventType, setEvent] = useState("");
  const [days, setDays] = useState("7");
  const [applied, setApplied] = useState(null);

  const load = useCallback(async (newTake = PAGE_SIZE, f = { user, eventType, days }) => {
    setTake(newTake);
    const data = await run(getActivityFeed, { actorEmail: f.user, eventType: f.eventType, days: parseInt(f.days), take: newTake + 1 });
    if (data) {
      setHasMore(data.length > newTake);
      setRows(data.slice(0, newTake));
      setApplied({ filtered: Boolean(f.user || f.eventType), days: f.days });
    }
  }, [run, user, eventType, days]);

  const loadMore = useCallback(() => {
    load(take + PAGE_SIZE);
  }, [load, take]);

  const clearFilters = () => {
    setUser("");
    setEvent("");
    load(PAGE_SIZE, { user: "", eventType: "", days });
  };

  useEffect(() => {
    load();
    run(getActivityMeta).then(d => {
      if (d) {
        setUsers(d.users || []);
        setEventTypes(d.eventTypes || []);
      }
    });
  }, []); // eslint-disable-line

  return (
    <>
      <PageHead
        viewId="activity"
        description="Claude Enterprise audit events from the Compliance API activity feed, polled every 15 minutes."
        actions={
          <button className="btn btn--secondary" type="button" onClick={() => load()} disabled={loading}>
            <Icon name="refresh" size={16} /> Refresh
          </button>
        }
      />
      <div className="content">
        <ErrorAlert message={error} />
        <form className="card filterbar" onSubmit={e => { e.preventDefault(); load(); }}>
          <div className="card__body filters">
            <Field id="activity-user" label="User">
              <select id="activity-user" className="filterfield" value={user} onChange={e => setUser(e.target.value)}>
                <option value="">All users</option>
                {users.map(u => <option key={u}>{u}</option>)}
              </select>
            </Field>
            <Field id="activity-event" label="Event type">
              <select id="activity-event" className="filterfield" value={eventType} onChange={e => setEvent(e.target.value)}>
                <option value="">All events</option>
                {eventTypes.map(t => <option key={t}>{t}</option>)}
              </select>
            </Field>
            <Field id="activity-range" label="Time range">
              <select id="activity-range" className="filterfield" value={days} onChange={e => setDays(e.target.value)}>
                {RANGES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </Field>
            <button className="btn btn--primary btn--sm" type="submit" disabled={loading}>
              <Icon name="search" size={16} /> Search
            </button>
          </div>
        </form>

        {loading || (!applied && !error) ? (
          <Loading label="Loading activity…" />
        ) : rows.length === 0 ? (
          error ? null : applied.filtered ? (
            <Empty
              title="No activity matches these filters."
              detail={`Nothing for the selected user or event type in ${rangePhrase(applied.days)}.`}
              action={<button className="btn btn--secondary btn--sm" type="button" onClick={clearFilters}>Clear filters</button>}
            />
          ) : (
            <Empty title={`No activity in ${rangePhrase(applied.days)}.`} />
          )
        ) : (
          <>
            <p className="summary">
              <strong>{rows.length}{hasMore ? "+" : ""}</strong> event{rows.length !== 1 ? "s" : ""} · newest first
            </p>
            <div className="grid-scroll">
              <table className="grid">
                <thead>
                  <tr>
                    <th>Time</th><th>User</th><th>Event</th><th>Chat ID</th><th>IP</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => {
                    const { local, domain } = splitEmail(r.ActorEmail);
                    return (
                      <tr key={r.ActivityId}>
                        <td className="nowrap cell-muted">{formatTime(r.CreatedAt)}</td>
                        <td>
                          <strong>{local}</strong>
                          {domain && <span className="cell-muted">@{domain}</span>}
                        </td>
                        <td>
                          <span className={`tag ${EVENT_TAG[r.EventType] || ""}`}>
                            {(r.EventType || "").replace("claude_", "")}
                          </span>
                        </td>
                        <td className="cell-muted cell-truncate">{r.ChatId || "—"}</td>
                        <td className="cell-muted nowrap">{r.ActorIp || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {hasMore && (
              <div className="loadmore">
                <button className="btn btn--secondary" type="button" onClick={loadMore} disabled={loading}>
                  Load more
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
